import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '@store';
import { selectPimVehicleList } from './pimVehicleListSelectors';
import {
  selectIsStockSaleNEXTOrder,
  selectIsStockSaleOrder,
  selectOrder,
  selectOrderEvents,
  selectReserveDetailsFromOrder,
  selectSelectedSerialNumberFromOrder,
  selectSnapshot,
  selectVehicleOrderStatusFromOrder,
} from './order';
import { selectIsUsedCar } from './commonVehicleSelectors';
import { BosOrderStatus, MAKE, OrderType, invokeProvidedBrandSpecificCode, isElectricCar } from 'next-common';
import { showUsedCarDeleteButton } from '@utils/sales/order';
import { selectVehicleConfiguration } from './vehicleConfigurationSelectors';
import { hasReservationBlockingCancellation, isIFSReservationInProgress } from '@utils';

export const selectIsCalled = (state: RootState) => {
  return state.salesWizard?.isCalled;
};

export const selectStepMenuData = (state: RootState) => {
  return state.salesWizard?.stepMenuData;
};

export const selectCalculationId = (state: RootState) => {
  return state.salesWizard?.calculationId;
};

export const selectIsTradeInClicked = (state: RootState) => {
  return state.salesWizard?.tradeInClicked;
};

export const selectIsSalesWizardLoading = (state: RootState) => {
  return state.salesWizard?.isLoading;
};

export const selectIsReserveLoading = (state: RootState) => {
  return state.salesWizard?.isReserveLoading;
};

export const selectIsPickerLoading = (state: RootState) => {
  return state.salesWizard?.isPickerLoading;
};

export const selectIsVehicleLoading = (state: RootState) => {
  return state.salesWizard?.isVehicleLoading;
};

export const selectIsVehicleConfigLoading = (state: RootState) => {
  return state.salesWizard?.isVehicleConfigLoading;
};

export const selectIsVehicleListDataLoaded = (state: RootState) => {
  return state.salesWizard?.isVehicleListDataLoaded;
};

export const selectIsUnavailablePimData = (state: RootState) => {
  return state.salesWizard?.isUnAvailablePimData;
};

export const selectIsChangeVehicle = (state: RootState) => {
  return state.salesWizard?.isChangeVehicle;
};

export const selectIsPCodePageDisabled = (state: RootState) => {
  return state.salesWizard?.isPCodePageDisabled;
};

export const selectInvalidPcodesValidation = (state: RootState) => {
  return state.salesWizard?.invalidPcodesValidation;
};

// TODO : Change this to implied selector
export const selectMissingFieldsWarningMessage = (state: RootState) => {
  return state.salesWizard?.missingFieldsWarningMessage;
};

export const selectNotifications = (state: RootState) => {
  return state.salesWizard?.notifications;
};

export const selectReserveCancelModalStatus = (state: RootState) => {
  return state.salesWizard?.reserveCancelModalStatus;
};

export const selectOptimisticReserveFailModalStatus = (state: RootState) => {
  return state.salesWizard?.optimisticReserveFailModalStatus;
};

export const selectExpectedProductionDate = (state: RootState) => {
  return state.salesWizard.expectedProductionDate;
};

export const selectIsShowDeleteModel = (state: RootState) => {
  return state.salesWizard?.isShowDeleteModel;
};

export const selectIsVehicleValidForProceed = (state: RootState) => {
  return state.salesWizard?.isVehicleValidForProceed;
};

export const selectIsOldConfig = (state: RootState) => {
  return state.salesWizard?.isOldConfig;
};

export const selectIsVehicleSelected = (state: RootState) => {
  return state.salesWizard?.isVehicleSelected;
};

export const selectVehicleLocation = (state: RootState) => {
  return state.salesWizard?.vehicleLocation;
};

export const selectVehicleStatus = (state: RootState) => {
  return state.salesWizard?.vehicleStatus;
};

export const selectIsVehicleRemoved = (state: RootState) => {
  return state.salesWizard?.isVehicleRemoved;
};

export const selectExactMatchCount = (state: RootState) => {
  return state.salesWizard?.exactMatchCount;
};

export const selectNonExactMatchCount = (state: RootState) => {
  return state.salesWizard?.nonExactMatchCount;
};

export const selectRimsEquipments = (state: RootState) => {
  return state.salesWizard?.rimsEquipments;
};

export const selectSelectableEquipments = (state: RootState) => {
  return state.salesWizard?.selectableEquipments;
};

export const selectListverkEquipments = (state: RootState) => {
  return state.salesWizard?.listverkEquipments;
};

export const selectTaklakkEquipments = (state: RootState) => {
  return state.salesWizard?.taklakkEquipments;
};

export const selectSelectedVariantPath = (state: RootState) => {
  return state.salesWizard?.selectedVariantPath;
};

export const selectModelOptions = (state: RootState) => {
  return state.salesWizard?.modelOptions;
};

export const selectIsStockLoading = (state: RootState) => {
  return state.salesWizard?.isStockLoading;
};

export const selectConfigurationBuildability = (state: RootState) => {
  return state.salesWizard?.configurationBuildability;
};

export const selectReserveButtonStage = (state: RootState) => {
  return state.salesWizard?.reserveButtonStage;
};

export const selectIsSelectedVehicleUnavailable = (state: RootState) => {
  return state.salesWizard?.isSelectedVehicleUnavailable;
};

export const selectSalesWizardVehicleData = (state: RootState) => {
  return state.salesWizard?.salesWizardVehicleData;
};

export const selectVehicleColorId = (state: RootState) => {
  return state.salesWizard?.vehicleColorId;
};

export const selectVehicleInteriorId = (state: RootState) => {
  return state.salesWizard?.vehicleInteriorId;
};

export const selectOfferMake = (state: RootState) => {
  return state.salesWizard?.offerMake;
};

export const selectVehicleSku = (state: RootState) => {
  return state.salesWizard?.vehicleSku;
};

export const selectVehicleReservedStatus = (state: RootState) => {
  return state.salesWizard?.vehicleReservedStatus;
};

export const selectVehicleModelCode = (state: RootState) => {
  return state.salesWizard?.vehicleModelCode;
};

export const selectVehicleModelId = (state: RootState) => {
  return state.salesWizard?.vehicleModelId;
};

export const selectVehicleVariantId = (state: RootState) => {
  return state.salesWizard?.vehicleVariantId;
};

export const selectColorAvailableInPimFlag = (state: RootState) => {
  return state.salesWizard?.colorAvailableInPimFlag;
};

export const selectInteriorAvailableInPimFlag = (state: RootState) => {
  return state.salesWizard?.interiorAvailableInPimFlag;
};

export const selectBasePrice = (state: RootState) => {
  return state.salesWizard?.basePrice;
};

export const selectMappedEquipment = (state: RootState) => {
  return state.salesWizard?.mappedEquipment;
};

export const selectSelectedModelName = (state: RootState) => {
  return state.salesWizard?.selectedModelName;
};

export const selectSelectedVariant = (state: RootState) => {
  return state.salesWizard?.selectedVariant;
};

export const selectSelectedColorName = (state: RootState) => {
  return state.salesWizard?.selectedColorName;
};

export const selectSelectedInteriorName = (state: RootState) => {
  return state.salesWizard?.selectedInteriorName;
};

export const selectArticleCodeForLocalOption = (state: RootState) => {
  return state.salesWizard?.articleCodeForLocalOption;
};

export const selectShowCustomerPopup = (state: RootState) => {
  return state.salesWizard?.showCustomerPopup;
};

export const selectSoftLockOrderId = (state: RootState) => {
  return state.salesWizard?.softLockOrderId;
};

export const selectSubFamilySeries = (state: RootState) => {
  return state.salesWizard?.subFamilySeries;
};

export const selectAddedOptions = (state: RootState) => {
  return state.salesWizard?.addedOptions;
};

export const selectRemovedOptions = (state: RootState) => {
  return state.salesWizard?.removedOptions;
};

export const selectShowOptionPopup = (state: RootState) => {
  return state.salesWizard?.showOptionPopup;
};

export const selectIsNotBuildable = (state: RootState) => {
  return state.salesWizard?.isNotBuildable;
};

export const selectIsNotHealable = (state: RootState) => {
  return state.salesWizard?.isNotHealable;
};

export const selectPendingShareCode = (state: RootState) => {
  return state.salesWizard?.pendingShareCode;
};

export const selectVehicleFilterCounts = (state: RootState) => {
  return state.salesWizard?.vehicleFiltersWithCounts;
};

export const selectSelectedVehicleFilters = (state: RootState) => {
  return state.salesWizard?.selectedVehicleFilters;
};

export const selectVehicleCountBySeller = (state: RootState) => {
  return state.salesWizard?.vehicleCountBySeller;
};

export const selectIsVehicleFiltersCountLoading = (state: RootState) => {
  return state.salesWizard?.isFiltersCountLoading;
};

// Implied
export const selectIsMenuLoading = createSelector(
  [
    selectIsSalesWizardLoading,
    selectIsPickerLoading,
    selectIsVehicleLoading,
    selectIsVehicleConfigLoading,
    selectIsVehicleListDataLoaded,
  ],
  (isLoading, isPickerLoading, isVehicleLoading, vehicleConfigLoading, isVehicleListDataLoaded) => {
    return isLoading || isPickerLoading || isVehicleLoading || vehicleConfigLoading || isVehicleListDataLoaded;
  }
);

export const selectSelectedSerialNoAsNumber = createSelector([selectSelectedSerialNumberFromOrder], (serialNo) => {
  return Number(serialNo);
});

export const selectHasSelectedVehicle = createSelector(
  [selectPimVehicleList, selectIsStockSaleOrder, selectSelectedSerialNoAsNumber],
  (pimVehicleList, isStockSaleOrder, selectedSerialNo) => {
    return !!pimVehicleList || (isStockSaleOrder && !!selectedSerialNo);
  }
);

export const selectIsDeliveryCost = createSelector([selectMissingFieldsWarningMessage], (message) => {
  return message?.includes('leveringskostnader');
});

export const selectIsVehicleOrderNotSuccess = createSelector(
  [selectVehicleOrderStatusFromOrder],
  (vehicleOrderStatus) => {
    return vehicleOrderStatus &&
      [OrderType[OrderType.DEMO], OrderType[OrderType.ORDER], OrderType[OrderType.RESERVE]].includes(
        vehicleOrderStatus.orderType
      )
      ? vehicleOrderStatus?.status !== BosOrderStatus[BosOrderStatus.SUCCESS]
      : true;
  }
);

export const selectIsVehicleNotReserved = createSelector(
  [selectReserveDetailsFromOrder, selectOrder],
  (reserveDetails, order) => {
    if (isIFSReservationInProgress(order)) return false;
    return reserveDetails?.vehicleReserveStatus
      ? !hasReservationBlockingCancellation(reserveDetails)
      : !order?.nextOrderReserveStatus;
  }
);

export const selectShowAbortFactoryOrderAction = createSelector(
  [selectOrder, selectIsVehicleOrderNotSuccess, selectIsVehicleNotReserved],
  (order, isVehicleOrderNotSuccess, isVehicleNotReserved) => {
    return order?.isEditOrder ? isVehicleOrderNotSuccess : isVehicleNotReserved;
  }
);

export const selectShouldShowDeleteButton = createSelector(
  [selectIsUsedCar, selectOrderEvents, selectIsStockSaleNEXTOrder, selectShowAbortFactoryOrderAction],
  (isUsedCar, events, isStockSaleNEXTOrder, showAbortFactoryOrderAction) => {
    if (isUsedCar) return showUsedCarDeleteButton(isUsedCar, events);
    if (!isStockSaleNEXTOrder) return showAbortFactoryOrderAction;
    return false;
  }
);

export const selectShouldShowCartFooterButton = createSelector(
  [selectIsStockSaleOrder, selectOrder, selectVehicleVariantId, selectVehicleModelId],
  (isStockSaleOrder, order, selectedVariantId, selectedModelId) => {
    if (isStockSaleOrder) return true;
    if (order?.externalPricing?.shareCode) return true;
    if (invokeProvidedBrandSpecificCode(MAKE.Kia, { order })) {
      return !!selectedVariantId;
    } else {
      return !!selectedModelId;
    }
  }
);

export const selectIsElectricCar = createSelector(
  [selectSnapshot, selectVehicleConfiguration],
  (pimSnapshot, vehicle) => {
    return isElectricCar(pimSnapshot, vehicle);
  }
);

export const selectHasUpdateEventOccurs = (state: RootState) => {
  return state.salesWizard?.hasUpdateEventOccurs;
};
